/** What every step's page says the same way: its heading, its cards, and its Submit. */
import type { ReactNode } from 'react';
import { Card, CardContent } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { RadioGroupItem } from '~/components/ui/radio-group';
import { formatChecklistDate, KEPT_AS_IT_IS } from '~/pages/FamilyDetail/checklistWords';
import { formatCurrency } from '~/utils/paymentUtils';
import type { AgreementContext, ChecklistStepView } from '../useChecklistStep';

/** The paths a step page goes to once it's submitted. */
export interface StepPaths {
  /** The year's enroll page. */
  overviewPath: string;
  /** A step of the same year, by its item. */
  stepPath: (itemId: string) => string;
}

/** Opens Who's coming ready to change, done or not, while the family still can. */
export const CHANGE_PARAM = 'change';

/** Who's coming, by its item, opened ready to change. */
export function changeWhoPath(stepPath: (itemId: string) => string, whoItemId: string): string {
  return `${stepPath(whoItemId)}?${CHANGE_PARAM}=1`;
}

/** What every step page is given. */
export interface StepProps extends StepPaths {
  step: ChecklistStepView;
  /** A done step, or the office viewing as the family: the answers, and nothing to change. */
  readOnly: boolean;
  /** The office viewing as the family: nothing on the page changes anything, whatever the step allows. */
  viewingAs?: boolean;
}

/** What a finished step says in place of a form; an updated agreement the school kept as it is says so, as the office reads it. */
export function finishedLine(state: string, completedAt: string | null | undefined, notNeeded = "This step isn't needed.",
  keptAsItIs = false): string | null {
  if (state === 'DONE') return completedAt ? `Done ${formatChecklistDate(completedAt)}` : 'Done';
  if (state === 'WAIVED') return keptAsItIs ? KEPT_AS_IT_IS : 'The school waived this step.';
  if (state === 'NOT_NEEDED') return notNeeded;
  return null;
}

export const selectClass =
  'h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base ring-offset-background ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-10 md:text-sm';

export function StepHeader({ step, subtitle, notNeeded }: { step: ChecklistStepView; subtitle?: ReactNode; notNeeded?: string }) {
  const finished = finishedLine(step.item.state, step.item.completedAt, notNeeded, step.item.keptAsItIs);
  return (
    <header className="space-y-1">
      <h1 className="text-2xl font-bold md:text-3xl">{step.item.title}</h1>
      <p className="text-muted-foreground">{subtitle ?? step.yearName}</p>
      {finished ? <p className="text-sm font-medium text-green-700">{finished}</p> : null}
      {step.intro ? <p className="pt-2">{step.intro}</p> : null}
    </header>
  );
}

export function Section({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4 md:p-6">{children}</CardContent>
    </Card>
  );
}

/** The server's refusals, the save line, and the button that submits. */
export function StepFooter({ errors, saveState, submitting, label, onSubmit, disabled = false }: {
  errors: string[];
  saveState: 'idle' | 'saving' | 'saved';
  submitting: boolean;
  label: string;
  onSubmit: () => void;
  /** Off until the step has what it submits, such as a household still loading. */
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3">
      {errors.length > 0 ? (
        <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
          <ul className="list-disc space-y-1 pl-5">
            {errors.map((error, index) => <li key={index}>{error}</li>)}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Your answers are saved.' : ''}
        </p>
        <Button type="button" size="lg" className="min-h-11 w-full md:w-auto" disabled={submitting || disabled} onClick={onSubmit}>
          {submitting ? 'Submitting…' : label}
        </Button>
      </div>
    </div>
  );
}

export function Choice({ id, value, label }: { id: string; value: string; label: string }) {
  return (
    <Label
      htmlFor={id}
      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-base font-normal has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
    >
      <RadioGroupItem id={id} value={value} className="h-5 w-5" />
      <span>{label}</span>
    </Label>
  );
}

export function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/** "Jan 20" from a date-only string, without shifting a day. */
function monthDay(value: string): string {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * Each child's price, then any discount or aid and the total. A part-year child's price is the server's, for their part of
 * the year, and the line under it says when they start and what share of the full price that is.
 */
export function AgreementTuition({ agreement, totalLabel = 'Total' }: { agreement: AgreementContext; totalLabel?: string }) {
  return (
    <Section>
      <h2 className="text-lg font-semibold">Tuition</h2>
      <ul className="space-y-2">
        {agreement.children.map((child, index) => (
          <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="min-w-0 break-words">
              <span className="font-medium">{child.name}</span>
              {child.programName ? <span className="text-muted-foreground"> · {child.programName}</span> : null}
            </span>
            <span className="tabular-nums">{formatCurrency(child.partYearCents ?? child.costCents)}</span>
            {child.startsOn && child.shareOfYear ? (
              <span className="basis-full text-sm text-muted-foreground">
                Starts {monthDay(child.startsOn)} · {child.shareOfYear} of {formatCurrency(child.costCents)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <dl className="space-y-1 border-t pt-3">
        {agreement.discountCents ? <TotalRow label="Discount" cents={-Math.abs(agreement.discountCents)} /> : null}
        {agreement.aidCents ? <TotalRow label="Financial aid" cents={-Math.abs(agreement.aidCents)} /> : null}
        {agreement.totalCents !== null && agreement.totalCents !== undefined ? <TotalRow label={totalLabel} cents={agreement.totalCents} strong /> : null}
      </dl>
    </Section>
  );
}

function TotalRow({ label, cents, strong = false }: { label: string; cents: number; strong?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? 'font-semibold' : ''}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{cents < 0 ? `−${formatCurrency(-cents)}` : formatCurrency(cents)}</dd>
    </div>
  );
}

/** The agreement's own words. */
export function AgreementTerms({ agreement }: { agreement: AgreementContext }) {
  if (!agreement.terms?.intro && !agreement.terms?.terms) return null;
  return (
    <Section>
      {agreement.terms.intro ? <p className="whitespace-pre-line break-words">{agreement.terms.intro}</p> : null}
      {agreement.terms.terms ? <p className="whitespace-pre-line break-words">{agreement.terms.terms}</p> : null}
    </Section>
  );
}
